import { LogIn } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";

/**
 * Shown while an admin is signed in as somebody else.
 *
 * Deliberately not the same as ImpersonationBanner, which sits in the same corner of the same
 * layout and says "Viewing as". The two features look identical from the outside and are not:
 * that one only changes which rows are read, this one changes who the server thinks you are,
 * so anything generated here spends the MEMBER's credits. Somebody who cannot tell the two
 * apart at a glance will eventually use the wrong one and be surprised by the bill, which is
 * why the wording, the color and the icon are all different.
 */
export function ProfileSwitchBanner() {
  const { isSwitched, originalEmail, switching, switchBack } = useProfileSwitch();
  const { user } = useAuth();

  if (!isSwitched) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-brand-pink text-white px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium shadow-md">
      <LogIn className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Signed in as {user?.email ?? "another account"}
        {/* Said here rather than left to be discovered: the session lives in localStorage, so
            every tab in this browser is this account until you go back. */}
        <span className="hidden sm:inline opacity-80"> — applies to every tab in this browser</span>
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={switching}
        className="h-7 gap-1 bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
        onClick={switchBack}
      >
        Back to {originalEmail ? originalEmail.split("@")[0] : "my account"}
      </Button>
    </div>
  );
}
